import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  getConfiguredSnapshotDate,
  getAvailableSnapshots,
  setConfiguredSnapshotDate,
  removeConfiguredSnapshotDate
} from '@/lib/supabase/snapshotConfig';
import { AlertCircle, Loader2 } from 'lucide-react';

interface SnapshotInfo {
  snapshot_date: string;
  record_count: number;
  is_latest: boolean;
  is_configured: boolean;
}

export const SnapshotDateConfig: React.FC = () => {
  const [snapshots, setSnapshots] = useState<SnapshotInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState(false);
  const [currentConfiguredDate, setCurrentConfiguredDate] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { toast } = useToast();

  // Load available snapshots and current configuration
  const loadSnapshots = async () => {
    try {
      setLoading(true);

      // Get available snapshots and current configuration in parallel
      const [snapshotData, configuredDate] = await Promise.all([
        getAvailableSnapshots(),
        getConfiguredSnapshotDate()
      ]);

      setCurrentConfiguredDate(configuredDate);

      // Build snapshot info array
      const snapshotInfo: SnapshotInfo[] = snapshotData.map((snapshot, index) => ({
        snapshot_date: snapshot.snapshot_date,
        record_count: snapshot.record_count,
        is_latest: index === 0,
        is_configured: snapshot.snapshot_date === configuredDate
      }));

      setSnapshots(snapshotInfo);

      // Set default selection to configured date or latest
      if (!selectedDate) {
        const defaultDate = configuredDate || snapshotData[0]?.snapshot_date || '';
        setSelectedDate(defaultDate);
      }
    } catch (error) {
      console.error('Failed to load snapshots:', error);
      toast({
        title: 'Error',
        description: 'Failed to load snapshot information',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Update snapshot date configuration
  const updateSnapshotConfiguration = async (snapshotDate: string) => {
    try {
      console.log('[SnapshotDateConfig] updateSnapshotConfiguration called with:', snapshotDate);
      setConfiguring(true);

      console.log('[SnapshotDateConfig] Calling setConfiguredSnapshotDate...');
      await setConfiguredSnapshotDate(snapshotDate, 'admin'); // TODO: Replace with actual user ID
      console.log('[SnapshotDateConfig] setConfiguredSnapshotDate completed');

      // Refresh data
      console.log('[SnapshotDateConfig] Refreshing snapshots...');
      await loadSnapshots();
      console.log('[SnapshotDateConfig] Snapshots refreshed');

      toast({
        title: 'Success',
        description: `Snapshot date updated to ${new Date(snapshotDate).toLocaleDateString()}`,
        variant: 'default'
      });

      setShowConfirmDialog(false);
    } catch (error) {
      console.error('Failed to update snapshot configuration:', error);
      toast({
        title: 'Error',
        description: 'Failed to update snapshot date configuration',
        variant: 'destructive'
      });
    } finally {
      setConfiguring(false);
    }
  };

  const resetToLatest = async () => {
    try {
      setConfiguring(true);

      await removeConfiguredSnapshotDate();

      // Refresh data
      await loadSnapshots();

      toast({
        title: 'Success',
        description: 'Reset to use latest snapshot date',
        variant: 'default'
      });
    } catch (error) {
      console.error('Failed to reset configuration:', error);
      toast({
        title: 'Error',
        description: 'Failed to reset snapshot configuration',
        variant: 'destructive'
      });
    } finally {
      setConfiguring(false);
    }
  };

  const handleConfigureSnapshot = () => {
    if (!selectedDate) return;
    setShowConfirmDialog(true);
  };

  const formatDate = (dateString: string) => {
    // Handle date-only format (YYYY-MM-DD) and timestamp format
    const date = dateString.includes('T')
      ? new Date(dateString)
      : new Date(dateString + 'T00:00:00.000Z'); // Treat date-only as UTC midnight
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC' // Use UTC to avoid timezone conversion issues
    });
  };

  const formatDateTime = (dateString: string) => {
    // Handle date-only format (YYYY-MM-DD) and timestamp format
    const date = dateString.includes('T')
      ? new Date(dateString)
      : new Date(dateString + 'T00:00:00.000Z'); // Treat date-only as UTC midnight
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC' // Use UTC to avoid timezone conversion issues
    });
  };

  useEffect(() => {
    loadSnapshots();
  }, []);

  if (loading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-primary' />
        <span className='ml-2 text-sm'>Loading snapshot information...</span>
      </div>
    );
  }

  return (
    <div className='space-y-2'>
      {/* Configuration Selection */}
      <Select value={selectedDate} onValueChange={setSelectedDate}>
        <SelectTrigger className='h-8'>
          <SelectValue placeholder='Choose date...' />
        </SelectTrigger>
        <SelectContent>
          {snapshots.map(snapshot => (
            <SelectItem key={snapshot.snapshot_date} value={snapshot.snapshot_date}>
              <div className='flex items-center gap-2'>
                <span className='text-xs'>{formatDateTime(snapshot.snapshot_date)}</span>
                {snapshot.is_latest && (
                  <Badge variant='default' className='text-xs'>
                    Latest
                  </Badge>
                )}
                {snapshot.is_configured && (
                  <Badge variant='secondary' className='text-xs'>
                    Current
                  </Badge>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Action Buttons */}
      <div className='flex gap-1'>
        <Button
          onClick={handleConfigureSnapshot}
          disabled={!selectedDate || configuring || selectedDate === currentConfiguredDate}
          size='sm'
          className='flex-1 h-7 text-xs'
        >
          {configuring ? <Loader2 className='h-3 w-3 animate-spin' /> : 'Apply'}
        </Button>
        {currentConfiguredDate && (
          <Button
            variant='outline'
            size='sm'
            onClick={resetToLatest}
            disabled={configuring}
            className='h-7 text-xs'
          >
            Reset
          </Button>
        )}
      </div>

      {/* Compact Warning */}
      {selectedDate && snapshots.find(s => s.snapshot_date === selectedDate && !s.is_latest) && (
        <div className='flex items-center gap-1 p-1 bg-yellow-50 rounded text-xs text-yellow-700'>
          <AlertCircle className='h-3 w-3' />
          <span>Historical</span>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure Snapshot Date</DialogTitle>
            <DialogDescription>
              Are you sure you want to configure the system to use the snapshot from{' '}
              <strong>{selectedDate && formatDateTime(selectedDate)}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className='py-4'>
            <div className='flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200'>
              <AlertCircle className='h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0' />
              <div className='text-sm text-blue-800'>
                <p className='font-medium'>This will affect:</p>
                <ul className='list-disc list-inside mt-1 space-y-1'>
                  <li>Recall Map - all visible recall markers</li>
                  <li>Recall List - all recall data and filtering</li>
                  <li>All users will see data from this snapshot</li>
                </ul>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => updateSnapshotConfiguration(selectedDate)}
              disabled={configuring}
            >
              {configuring ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                  Applying...
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
